import { ReauthError } from "../../hooks/useApi";
import { providerMeta } from "../providerMeta";

/**
 * Shared error display. Reauth errors (expired/revoked refresh token) get a
 * Reconnect button that restarts the OAuth flow — naming the account, since
 * users with several accounts otherwise can't tell which one expired.
 */
export function ErrorState({ error, compact }: { error: Error; compact?: boolean }) {
  const needsReauth = error instanceof ReauthError || error.name === "ReauthError";
  const reauth = needsReauth ? (error as ReauthError) : null;
  // Server messages ("Re-authentication required for viktor@…") already name
  // the account, so fall back to them when the structured field is missing.
  const reauthText = reauth?.account
    ? `${reauth.account} needs to be signed in again.`
    : reauth?.message || "This account needs to be signed in again.";
  const baseLoginUrl =
    (reauth?.provider && providerMeta(reauth.provider).loginUrl) || "/auth/login";
  const loginUrl = reauth?.account
    ? `${baseLoginUrl}?hint=${encodeURIComponent(reauth.account)}`
    : baseLoginUrl;
  const isImap = reauth?.provider === "imap";

  const content = (
    <div className={`text-center ${compact ? "px-4 py-3" : ""}`}>
      <p className="text-[13px] text-ink-muted">{needsReauth ? reauthText : error.message}</p>
      {needsReauth &&
        (isImap ? (
          <p className="mt-2 text-[12px] text-ink-muted">
            Open Settings → Accounts to update the app password.
          </p>
        ) : (
          <a
            href={loginUrl}
            className="mt-2 inline-block rounded bg-accent px-4 py-1.5 text-[13px] font-medium text-white hover:bg-accent-hover"
          >
            Reconnect{reauth?.account ? ` ${reauth.account}` : ""}
          </a>
        ))}
    </div>
  );

  if (compact) return content;
  return <div className="grid h-full flex-1 place-items-center bg-surface">{content}</div>;
}
